import { useCallback, useState } from 'react'
import { isEmpty, get, sampleSize } from 'lodash'
import { parse, ParseResult } from 'papaparse'

import { IUploadProgress } from '../../types/ProcessUsersImportUploadTypes'
import getAWSSignatureV4Form from '../../../../utils/getAWSSignatureV4Form'
import { notification } from '../../../../components/common/notifications/Notifications'
import {
  UsersCsvImportModelFragment,
  UsersImportCredentialsFragment
} from '../../operations/UsersImportFragments.codegen'
import { useCreateUsersImportMutation } from '../operations/createUsersImport.codegen'
import { useCreateUsersImportUploadCredentialsMutation } from '../operations/createUsersImportUploadCredentials.codegen'
import { MAX_CSV_USER_IMPORT_LENGTH } from 'shared/validations'

const SAMPLE_SIZE = 3

export interface IProcessImportUploadData {
  processUpload: (file: File) => Promise<UsersCsvImportModelFragment>
  uploadProgress?: IUploadProgress
}

interface IXhrCallbacks {
  onComplete: (v: any) => void
  onError: () => void
  onProgress: (progress: IUploadProgress) => void
}

const buildXhr = ({
  onComplete,
  onError,
  onProgress
}: IXhrCallbacks): XMLHttpRequest => {
  const xhr = new XMLHttpRequest()

  xhr.upload.addEventListener('progress', onProgress, false)
  xhr.addEventListener('load', onComplete, false)
  xhr.addEventListener('error', onError, false)
  xhr.addEventListener('abort', onError, false)

  return xhr
}

const uploadToAws = async (
  file: File,
  { uploadUrl, params }: UsersImportCredentialsFragment,
  onProgress: (progress: IUploadProgress) => void
) => {
  return new Promise((onComplete, onError) => {
    const fd = getAWSSignatureV4Form(params, file)

    const xhr = buildXhr({
      onComplete,
      onError,
      onProgress
    })

    xhr.open('POST', uploadUrl, true)
    xhr.send(fd)
  })
}

const isInvalidSamples = (data): boolean =>
  data.some((sample) => sample.length !== data[0].length)

const validateFileAndGetSamples = async (file: File): Promise<string[][]> => {
  const { data, errors, meta } = await new Promise<ParseResult>(
    (resolve, reject) => {
      parse(file, {
        preview: MAX_CSV_USER_IMPORT_LENGTH,
        complete: resolve,
        error: (err) => {
          reject(err)
        },
        skipEmptyLines: 'greedy',
        delimiter: ','
      })
    }
  )
  if (meta.truncated) {
    throw new Error(
      `CSV is too big-looop only supports CSV uploads up to ${MAX_CSV_USER_IMPORT_LENGTH} lines`
    )
  }

  if (!isEmpty(errors)) {
    console.error(errors)
    throw new Error('Input is not a valid CSV file')
  }

  if (isInvalidSamples(data)) {
    throw new Error('Input is not a valid CSV file')
  }

  const sampleRows = sampleSize(data, SAMPLE_SIZE)

  return sampleRows[0].map((_field: string, idx: number) =>
    sampleRows.map((element) => element[idx])
  )
}

export const useUploadUsersImport = (): IProcessImportUploadData => {
  const [uploadCredentialsMutation] =
    useCreateUsersImportUploadCredentialsMutation()
  const [createUserImportMutation] = useCreateUsersImportMutation()

  const [uploadProgress, setUploadProgress] = useState<IUploadProgress>()

  const processUpload = useCallback(
    async (file: File): Promise<UsersCsvImportModelFragment> => {
      let samples
      try {
        samples = await validateFileAndGetSamples(file)
      } catch (error) {
        notification.error(error.message)
        throw error
      }

      const { data: uploadCredentialsResponse } =
        await uploadCredentialsMutation({
          variables: { filename: file.name }
        })

      const uploadCredentials: UsersImportCredentialsFragment = get(
        uploadCredentialsResponse,
        'admin.usersImport.uploadCredentials'
      )

      await uploadToAws(file, uploadCredentials, setUploadProgress)

      const { data: createUserImportResponse } = await createUserImportMutation(
        {
          variables: {
            samples,
            filename: uploadCredentials.params.key
          }
        }
      )

      return get(createUserImportResponse, 'admin.usersImport.createImport')
    },
    [setUploadProgress]
  )

  return {
    processUpload,
    uploadProgress
  }
}
